import { useState } from 'react';
import { Button } from '@/ui/Button';
import { Dialog } from '@/ui/Dialog';
import { Field, Input, Select, Textarea } from '@/ui/Field';
import { BuscadorDePersonas } from '@/components/BuscadorDePersonas';
import { useGenerarTarjeta, type PersonaDelDirectorio } from './api';

/**
 * EstudioDeTarjetas.tsx — generar la imagen de una felicitación.
 *
 * Cinco tipos. El quinto, «Nuevo Ingreso», **no se genera aquí**: tiene su
 * propio estudio con vista previa en vivo, y elegirlo salta a él. Eso ya lo
 * hacía el vanilla, pero cerrando su modal y abriendo el otro desde un
 * `onchange` — aquí es una prop, así que quien lo abre decide.
 */

const TIPOS: Array<[string, string, string]> = [
  ['cumpleanos', 'bi-cake2', 'Cumpleaños'],
  ['work_anniversary', 'bi-award', 'Aniversario laboral'],
  ['bienvenida', 'bi-person-plus', 'Bienvenida'],
  ['reconocimiento', 'bi-trophy', 'Reconocimiento'],
  ['nuevo_ingreso', 'bi-door-open', 'Nuevo Ingreso'],
];

export function EstudioDeTarjetas({
  abierto, alCerrar, alPedirNuevoIngreso,
}: {
  abierto: boolean;
  alCerrar: () => void;
  /** «Nuevo Ingreso» tiene su propio estudio; esto lo abre. */
  alPedirNuevoIngreso: () => void;
}) {
  const [tipo, setTipo] = useState('cumpleanos');
  const [persona, setPersona] = useState<PersonaDelDirectorio | null>(null);
  const [anios, setAnios] = useState(1);
  const [mensaje, setMensaje] = useState('');
  const [publicar, setPublicar] = useState(true);
  const [correo, setCorreo] = useState(true);
  const [vista, setVista] = useState<string | null>(null);
  const [error, setError] = useState('');

  const generar = useGenerarTarjeta();

  function limpiar() {
    setTipo('cumpleanos'); setPersona(null); setAnios(1); setMensaje('');
    setPublicar(true); setCorreo(true); setVista(null); setError('');
    generar.reset();
  }
  function cerrar() { limpiar(); alCerrar(); }

  function cambiarTipo(nuevo: string) {
    if (nuevo === 'nuevo_ingreso') { cerrar(); alPedirNuevoIngreso(); return; }
    setTipo(nuevo);
  }

  function pedir(publicarla: boolean, alCorreo: boolean) {
    if (!persona) { setError('Selecciona un colaborador.'); return; }
    setError('');
    generar.mutate(
      {
        kind: tipo,
        user_id: persona.id,
        anios: tipo === 'work_anniversary' ? anios || 1 : null,
        message: mensaje.trim(),
        publish: publicarla,
        email_to_admin: alCorreo,
      },
      {
        onSuccess: (r) => {
          if (r.card_url) setVista(r.card_url);
          if (publicarla || alCorreo) {
            let m = 'Tarjeta generada.';
            if (r.post_id) m += ' Publicada en el feed.';
            if (r.emailed_to) m += ` Enviada a ${r.emailed_to}.`;
            Utils.toast(m, 'success');
          }
        },
        onError: (e: unknown) => setError((e as Error)?.message || 'No se pudo generar.'),
      },
    );
  }

  return (
    <Dialog
      abierto={abierto}
      onCambio={(v) => { if (!v) cerrar(); }}
      titulo="Estudio de tarjetas"
      ancho="max-w-3xl"
      pie={
        <>
          <Button variant="ghost" onClick={cerrar}>Cerrar</Button>
          <Button variant="secondary" onClick={() => pedir(false, false)} disabled={generar.isPending}>
            <i className="bi bi-eye" aria-hidden="true" /> Vista previa
          </Button>
          <Button onClick={() => pedir(publicar, correo)} disabled={generar.isPending}>
            <i className="bi bi-send" aria-hidden="true" />
            {generar.isPending ? 'Generando…' : 'Generar'}
          </Button>
        </>
      }
    >
      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <Field label="Tipo de tarjeta" className="mb-3">
            <Select value={tipo} onChange={(e) => cambiarTipo(e.target.value)}>
              {TIPOS.map(([v, , etiqueta]) => (
                <option key={v} value={v}>{etiqueta}</option>
              ))}
            </Select>
          </Field>

          <Field label="Colaborador" className="mb-3">
            <BuscadorDePersonas
              id="tarjeta-persona"
              etiqueta="Buscar al colaborador por su nombre"
              marcador="Buscar por nombre…"
              elegida={persona}
              alElegir={setPersona}
              alSoltar={() => setPersona(null)}
            />
          </Field>

          {tipo === 'work_anniversary' && (
            <Field label="Años de antigüedad" className="mb-3">
              <Input
                type="number"
                min={1}
                value={anios}
                onChange={(e) => setAnios(Number(e.target.value) || 1)}
              />
            </Field>
          )}

          <Field
            label="Mensaje"
            opcional
            ayuda="Si lo dejas vacío se usa un mensaje predefinido."
            className="mb-3"
          >
            <Textarea rows={3} value={mensaje} onChange={(e) => setMensaje(e.target.value)} />
          </Field>

          <label className="mb-1.5 flex items-center gap-2 text-2">
            <input type="checkbox" checked={publicar} onChange={(e) => setPublicar(e.target.checked)}
              className="size-4 accent-brand" />
            <span>Publicar en el feed</span>
          </label>
          <label className="flex items-center gap-2 text-2">
            <input type="checkbox" checked={correo} onChange={(e) => setCorreo(e.target.checked)}
              className="size-4 accent-brand" />
            <span>Enviar la tarjeta a mi correo (prueba)</span>
          </label>

          {error && <p role="alert" className="mt-3 text-3 text-danger-ink">{error}</p>}
        </div>

        <div>
          <p className="mb-1.5 text-3 font-semibold">Vista previa</p>
          <div className="flex min-h-60 items-center justify-center rounded border border-dashed
                          border-line-strong p-4 text-center text-3 text-muted">
            {generar.isPending && 'Generando…'}
            {!generar.isPending && vista && (
              /* `src` es una prop, no una interpolación en una plantilla. */
              <img src={vista} alt="Vista previa de la tarjeta" className="w-full rounded-sm" />
            )}
            {!generar.isPending && !vista && 'Elige tipo y colaborador, luego «Vista previa».'}
          </div>
        </div>
      </div>
    </Dialog>
  );
}
